import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";

import HeroSection from "@/components/layout/HeroSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Phone, HelpCircle, Shield, Euro, FileText, Clock, Users, AlertTriangle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import faqHero from "@/assets/faq-hero.jpg";
import { useImagePreload } from "@/hooks/useImagePreload";

const FAQ = () => {
  useImagePreload(faqHero);
  const faqs = [
    {
      category: "Grundlagen",
      icon: HelpCircle,
      questions: [
        {
          question: "Was ist eine Cyberversicherung?",
          answer: "Eine Cyberversicherung ist eine spezialisierte Versicherung, die finanzielle Schäden durch Cyberangriffe, Datenverlust und IT-Sicherheitsvorfälle abdeckt. Sie schützt Arztpraxen vor den wirtschaftlichen Folgen von Ransomware-Angriffen, Datendiebstahl, Betriebsunterbrechungen und DSGVO-Verstößen. Die Versicherung übernimmt sowohl Eigenschäden als auch Drittschäden und bietet im Schadensfall professionelle Unterstützung durch IT-Forensiker und Rechtsanwälte."
        },
        {
          question: "Warum brauchen Arztpraxen eine Cyberversicherung?",
          answer: "Arztpraxen verwalten hochsensible Patientendaten und sind besonders attraktive Ziele für Cyberkriminelle. Die Digitalisierung im Gesundheitswesen schreitet voran, gleichzeitig fehlt oft die IT-Sicherheitsexpertise. Ein erfolgreicher Cyberangriff kann die Praxis tagelang lahmlegen, zu massiven Umsatzausfällen führen und DSGVO-Bußgelder nach sich ziehen. Die durchschnittliche Schadenshöhe liegt bei über 50.000€ – ohne Versicherung kann dies existenzbedrohend sein."
        },
        {
          question: "Was kostet eine Cyberversicherung für Arztpraxen?",
          answer: "Die Kosten einer Cyberversicherung variieren je nach Praxisgröße, Anzahl der Mitarbeiter, IT-Infrastruktur und gewünschter Versicherungssumme. Für eine durchschnittliche Arztpraxis mit 3-5 Mitarbeitern liegen die Jahresbeiträge zwischen 800€ und 2.500€. Größere MVZ oder Kliniken zahlen entsprechend mehr. Die Investition ist jedoch minimal im Vergleich zu den potenziellen Schadenskosten von 50.000€ bis über 100.000€."
        }
      ]
    },
    {
      category: "Leistungen",
      icon: Shield,
      questions: [
        {
          question: "Welche Schäden deckt eine Cyberversicherung ab?",
          answer: "Eine Cyberversicherung deckt sowohl Eigenschäden als auch Drittschäden ab. Zu den Eigenschäden gehören: Datenwiederherstellung, IT-Forensik, Betriebsunterbrechung, Krisenmanagement, Rechtskosten und Lösegeldzahlungen bei Ransomware. Drittschäden umfassen: Schadensersatzforderungen von Patienten, DSGVO-Bußgelder, Vertragsstrafen und Rechtsverteidigungskosten. Zusätzlich sind oft Präventionsmaßnahmen wie Sicherheitstrainings für Mitarbeiter enthalten."
        },
        {
          question: "Was ist der Unterschied zwischen Eigenschaden und Drittschaden?",
          answer: "Eigenschäden sind Schäden, die direkt in Ihrer eigenen Praxis entstehen, z.B. Kosten für IT-Wiederherstellung, Umsatzausfall durch Betriebsunterbrechung oder Lösegeldzahlungen. Drittschäden betreffen hingegen Schäden, die Dritten (z.B. Patienten) durch einen Cybervorfall in Ihrer Praxis entstehen. Dazu gehören Schadensersatzforderungen von Patienten wegen Datenmissbrauchs, DSGVO-Bußgelder der Aufsichtsbehörden oder Vertragsstrafen gegenüber Geschäftspartnern."
        },
        {
          question: "Sind Lösegeldzahlungen bei Ransomware versichert?",
          answer: "Ja, die meisten Cyberversicherungen übernehmen Lösegeldzahlungen bei Ransomware-Angriffen. Allerdings gibt es hier Einschränkungen und Bedingungen: Die Zahlung muss mit dem Versicherer abgestimmt werden, es muss nachgewiesen sein, dass keine andere Möglichkeit der Datenwiederherstellung besteht, und die Zahlung darf nicht gegen gesetzliche Vorgaben verstoßen. Wichtig: Die Versicherung übernimmt nicht nur das Lösegeld, sondern auch die Kosten für Verhandlungen und IT-Forensik."
        }
      ]
    },
    {
      category: "Voraussetzungen",
      icon: FileText,
      questions: [
        {
          question: "Welche Voraussetzungen muss meine Praxis erfüllen?",
          answer: "Versicherer stellen bestimmte IT-Sicherheitsanforderungen, um eine Cyberversicherung abzuschließen. Dazu gehören: Regelmäßige Datensicherung (Backups), aktueller Virenschutz, Firewall, verschlüsselte Datenübertragung, Passwortrichtlinien und Mitarbeiterschulungen. Je besser Ihre IT-Sicherheit, desto günstiger wird der Versicherungsbeitrag. Viele Versicherer bieten zudem kostenlose IT-Sicherheitschecks an, um Schwachstellen zu identifizieren."
        },
        {
          question: "Brauche ich eine IT-Sicherheitszertifizierung?",
          answer: "Nein, eine formale IT-Sicherheitszertifizierung ist in der Regel nicht erforderlich. Allerdings müssen Sie nachweisen können, dass grundlegende IT-Sicherheitsmaßnahmen implementiert sind. Dies geschieht meist über einen Risikofragebogen des Versicherers. Bei größeren Praxen oder MVZ kann eine Vor-Ort-Prüfung durch IT-Experten des Versicherers erfolgen. Einige Premium-Tarife setzen höhere Sicherheitsstandards voraus."
        },
        {
          question: "Was passiert nach einem Schadenfall?",
          answer: "Im Schadensfall sollten Sie sofort den Versicherer informieren. Dieser schaltet ein spezialisiertes Incident-Response-Team ein, bestehend aus IT-Forensikern, Rechtsanwälten und Krisenmanagern. Die Experten analysieren den Angriff, stoppen die weitere Ausbreitung und stellen die Systeme wieder her. Parallel wird die rechtliche Situation geprüft und ggf. die Meldung bei Datenschutzbehörden übernommen. Sie werden während des gesamten Prozesses professionell betreut."
        }
      ]
    },
    {
      category: "Spezialfragen",
      icon: AlertTriangle,
      questions: [
        {
          question: "Gilt die Versicherung auch bei Home-Office und mobilem Arbeiten?",
          answer: "Ja, moderne Cyberversicherungen decken auch Schäden ab, die durch Home-Office und mobiles Arbeiten entstehen. Voraussetzung ist jedoch, dass auch für diese Arbeitsplätze IT-Sicherheitsstandards gelten: VPN-Verbindungen, verschlüsselte Geräte, aktuelle Antivirensoftware. Wichtig ist, dass Mitarbeiter im Umgang mit sensiblen Daten auch außerhalb der Praxis geschult sind. Bei BYOD (Bring Your Own Device) sollten Sie dies mit dem Versicherer abklären."
        },
        {
          question: "Sind auch Angriffe auf Drittdienstleister versichert?",
          answer: "Teilweise ja. Viele Cyberversicherungen decken auch Schäden ab, die durch Angriffe auf Ihre Dienstleister entstehen (z.B. Praxissoftware-Anbieter, Cloud-Provider). Dies nennt man Supply-Chain-Risiko. Allerdings gibt es hier oft Sublimits und Ausschlüsse. Prüfen Sie genau, welche Drittanbieter-Risiken versichert sind, besonders wenn Sie kritische Dienste auslagern. Bei Großangriffen auf Dienstleister kann es zu Einschränkungen kommen."
        },
        {
          question: "Wie schnell greift die Versicherung im Notfall?",
          answer: "Die meisten Cyberversicherungen bieten eine 24/7-Notfall-Hotline. Nach Meldung des Schadens wird innerhalb von 2-4 Stunden ein erstes Incident-Response-Team aktiviert. IT-Forensiker können oft noch am selben Tag vor Ort sein oder remote auf die Systeme zugreifen. Die Versicherung übernimmt die Koordination aller Maßnahmen. Wichtig: Je schneller Sie den Schaden melden, desto besser kann der Schaden begrenzt werden. Bewahren Sie die Notfallnummer gut auf!"
        },
        {
          question: "Was ist mit Social Engineering versichert?",
          answer: "Social Engineering (z.B. CEO Fraud, Phishing) ist in vielen Cyberversicherungen mitversichert, allerdings oft nur bis zu einem bestimmten Sublimit (z.B. 25.000€). Dabei werden Schäden durch Betrug übernommen, bei dem Mitarbeiter durch Täuschung zu Überweisungen oder Datenpreisgabe verleitet werden. Wichtig: Die Versicherung setzt voraus, dass Mitarbeiter regelmäßig zu diesem Thema geschult werden. Bewahren Sie Nachweise über Schulungen auf."
        },
        {
          question: "Kann ich die Versicherungssumme während der Laufzeit anpassen?",
          answer: "Ja, bei den meisten Versicherern können Sie die Versicherungssumme während der Vertragslaufzeit anpassen. Dies ist sinnvoll, wenn Ihre Praxis wächst, Sie neue IT-Systeme einführen oder sich Ihr Risikoprofil ändert. Eine Erhöhung der Versicherungssumme führt zu einer Beitragsnachzahlung, eine Reduzierung zu einer Beitragserstattung. Besprechen Sie Änderungen rechtzeitig mit Ihrem Versicherungsmakler, um lückenlosen Schutz zu gewährleisten."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="relative z-10 flex flex-col flex-1">
        <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "FAQ" }
        ]} />
        <CookieBanner />
        
        {/* Hero Section */}
        <HeroSection
          image={faqHero}
          alt="FAQ Cyberversicherung"
          title="Häufige Fragen zu Cyberversicherungen für Arztpraxen – Alle Antworten auf einen Blick"
          gradientPreset="none"
          imageOpacity={30}
          overlayColor="background"
          className="py-20 sm:py-24 lg:py-32 px-4"
        >
          <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
              <HelpCircle className="w-7 h-7 text-accent" />
            </div>
            
            <h1 className="heading-1 mb-6 text-foreground leading-tight">
              Häufig gestellte Fragen
            </h1>
            
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
                FAQ • Wissen • Support
              </span>
            </div>
            
            <p className="body-text text-foreground/70 max-w-2xl mx-auto">
              Alles, was Sie über Cyberversicherungen für Arztpraxen wissen müssen
            </p>
          </div>
        </HeroSection>


      {/* FAQ Content */}
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          {faqs.map((category, idx) => (
            <div key={idx} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <category.icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-foreground">{category.category}</h2>
              </div>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((faq, qIdx) => (
                  <AccordionItem 
                    key={qIdx} 
                    value={`${idx}-${qIdx}`}
                    className="border rounded-lg px-4 xs:px-5 sm:px-6 bg-card/30 backdrop-blur-sm"
                  >
                    <AccordionTrigger className="text-left text-sm xs:text-base sm:text-lg md:text-xl font-bold text-foreground hover:no-underline py-4 xs:py-5 sm:py-6">
                      <span className="pr-4">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-xs xs:text-sm md:text-base text-foreground/70 pb-4 xs:pb-5 sm:pb-6 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

        </div>
      </main>

      <CTASection 
        title="Haben Sie weitere Fragen?"
        subtitle="Kontaktieren Sie uns für eine persönliche Beratung"
      />

      <Footer />
      </div>
    </div>
  );
};

export default FAQ;
